import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState, useRef, useCallback, useLayoutEffect, useMemo } from 'react';
import { fetchSystemLinks } from '../services/api';
import { useTelegramModal } from '../context/TelegramModalContext';
import { PlaneIcon, SupportIcon, TermsInfoIcon, LeftArrowIcon, PlugIcon, MenuToggleIcon } from './Icons';
import logoImage from '../assets/logo_site.png?inline';
import { logger } from '../utils/logger';
import { HEADER_ACTION_DEFINITIONS, resolveHeaderPageConfig } from '../config/headerNavigation';

const DEFAULT_DESKTOP_MENU_WIDTH = 270;
const DESKTOP_MENU_FILLET_SIZE = 24;
const DESKTOP_MENU_BOTTOM_RADIUS = 28;
const MIN_DESKTOP_MENU_HEIGHT = DESKTOP_MENU_FILLET_SIZE + DESKTOP_MENU_BOTTOM_RADIUS + 1;
const DEFAULT_HEADER_METRICS = { width: 0, height: 0 };
const DESKTOP_MENU_SHELL_SHADOW_FILTER_ID = 'desktop-menu-shell-shadow-filter';
const EMPTY_HIDE_BUTTONS = {};

const HEADER_ICON_COMPONENTS = {
    plane: PlaneIcon,
    plug: PlugIcon,
    support: SupportIcon,
    terms: TermsInfoIcon,
};

function getDesktopMenuTotalWidth(dropdownWidth) {
    return dropdownWidth + DESKTOP_MENU_FILLET_SIZE;
}

function buildDesktopMenuShellPath(headerWidth, headerHeight, dropdownWidth, dropdownHeight, inset = 0) {
    const dropdownTotalWidth = getDesktopMenuTotalWidth(dropdownWidth);
    const safeHeaderWidth = Math.max(headerWidth, dropdownTotalWidth + headerHeight);
    const safeHeaderHeight = Math.max(headerHeight, 1);
    const safeDropdownHeight = Math.max(dropdownHeight, DESKTOP_MENU_FILLET_SIZE + DESKTOP_MENU_BOTTOM_RADIUS + 1);
    const left = inset;
    const top = inset;
    const right = safeHeaderWidth - inset;
    const headerBottom = safeHeaderHeight - inset;
    const shellBottom = safeHeaderHeight + safeDropdownHeight - inset;
    const headerRadius = Math.max((safeHeaderHeight / 2) - inset, 0);
    const filletRadius = Math.max(DESKTOP_MENU_FILLET_SIZE - inset, 0);
    const bottomRadius = Math.max(DESKTOP_MENU_BOTTOM_RADIUS - inset, 0);
    const dropdownLeft = safeHeaderWidth - dropdownTotalWidth + inset;
    const dropdownInnerX = dropdownLeft + DESKTOP_MENU_FILLET_SIZE;
    const topRightY = top + headerRadius;
    const bottomCurveTopY = shellBottom - bottomRadius;
    const bottomRightX = right - bottomRadius;
    const bottomLeftX = dropdownInnerX + bottomRadius;
    const filletBottomY = safeHeaderHeight + DESKTOP_MENU_FILLET_SIZE - inset;

    return [
        `M ${left + headerRadius} ${top}`,
        `H ${right - headerRadius}`,
        `A ${headerRadius} ${headerRadius} 0 0 1 ${right} ${topRightY}`,
        `V ${bottomCurveTopY}`,
        `A ${bottomRadius} ${bottomRadius} 0 0 1 ${bottomRightX} ${shellBottom}`,
        `H ${bottomLeftX}`,
        `A ${bottomRadius} ${bottomRadius} 0 0 1 ${dropdownInnerX} ${bottomCurveTopY}`,
        `V ${filletBottomY}`,
        `A ${filletRadius} ${filletRadius} 0 0 0 ${dropdownLeft} ${headerBottom}`,
        `H ${left + headerRadius}`,
        `A ${headerRadius} ${headerRadius} 0 0 1 ${left} ${headerBottom - headerRadius}`,
        `V ${top + headerRadius}`,
        `A ${headerRadius} ${headerRadius} 0 0 1 ${left + headerRadius} ${top}`,
        'Z',
    ].join(' ');
}

function buildDesktopMenuShellBorderPath(headerWidth, headerHeight, dropdownWidth, dropdownHeight, inset = 0.5) {
    const dropdownTotalWidth = getDesktopMenuTotalWidth(dropdownWidth);
    const safeHeaderWidth = Math.max(headerWidth, dropdownTotalWidth + headerHeight);
    const safeHeaderHeight = Math.max(headerHeight, 1);
    const safeDropdownHeight = Math.max(dropdownHeight, DESKTOP_MENU_FILLET_SIZE + DESKTOP_MENU_BOTTOM_RADIUS + 1);
    const right = safeHeaderWidth - inset;
    const headerBottom = safeHeaderHeight - inset;
    const shellBottom = safeHeaderHeight + safeDropdownHeight - inset;
    const filletRadius = Math.max(DESKTOP_MENU_FILLET_SIZE - inset, 0);
    const bottomRadius = Math.max(DESKTOP_MENU_BOTTOM_RADIUS - inset, 0);
    const dropdownLeft = safeHeaderWidth - dropdownTotalWidth + inset;
    const dropdownInnerX = dropdownLeft + DESKTOP_MENU_FILLET_SIZE;
    const bottomCurveTopY = shellBottom - bottomRadius;
    const bottomRightX = right - bottomRadius;
    const bottomLeftX = dropdownInnerX + bottomRadius;
    const filletBottomY = safeHeaderHeight + DESKTOP_MENU_FILLET_SIZE - inset;

    return [
        `M ${dropdownLeft} ${headerBottom}`,
        `A ${filletRadius} ${filletRadius} 0 0 1 ${dropdownInnerX} ${filletBottomY}`,
        `V ${bottomCurveTopY}`,
        `A ${bottomRadius} ${bottomRadius} 0 0 0 ${bottomLeftX} ${shellBottom}`,
        `H ${bottomRightX}`,
        `A ${bottomRadius} ${bottomRadius} 0 0 0 ${right} ${bottomCurveTopY}`,
        `V ${headerBottom}`,
    ].join(' ');
}

function Header({ hideButtons = EMPTY_HIDE_BUTTONS }) {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isDesktopMenuOpen, setIsDesktopMenuOpen] = useState(false);
    const [isNavigatingToTerms, setIsNavigatingToTerms] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [systemLinks, setSystemLinks] = useState({ bot: '', support: '' });
    const [desktopDropdownWidth, setDesktopDropdownWidth] = useState(DEFAULT_DESKTOP_MENU_WIDTH);
    const [desktopDropdownHeight, setDesktopDropdownHeight] = useState(MIN_DESKTOP_MENU_HEIGHT);
    const [headerMetrics, setHeaderMetrics] = useState(DEFAULT_HEADER_METRICS);
    const { openTelegramModal } = useTelegramModal();
    const location = useLocation();
    const supportLink = systemLinks.support || '';
    const botLink = systemLinks.bot || '';
    const disabledLinkStyle = { pointerEvents: 'none', opacity: 0.6, cursor: 'not-allowed' };
    const menuToggleRef = useRef(null);
    const headerRef = useRef(null);
    const desktopMenuRef = useRef(null);
    const dropdownRef = useRef(null);
    const dropdownBodyRef = useRef(null);
    const pageConfig = useMemo(() => resolveHeaderPageConfig(location.pathname, hideButtons), [location.pathname, hideButtons]);
    const showBackButton = pageConfig.showBackButton || isNavigatingToTerms;

    const closeAllMenus = useCallback(() => {
        setIsMenuOpen(false);
        setIsDesktopMenuOpen(false);
    }, []);

    const handleTermsNavigation = useCallback(() => {
        setIsNavigatingToTerms(true);
    }, []);

    const handleBotOpen = useCallback((e) => {
        e.preventDefault();
        if (botLink) {
            openTelegramModal(botLink);
        }
        closeAllMenus();
    }, [botLink, openTelegramModal, closeAllMenus]);

    const handleSupportClick = useCallback((e) => {
        if (!supportLink) {
            e.preventDefault();
            return;
        }

        openTelegramModal(supportLink);
        closeAllMenus();
    }, [supportLink, openTelegramModal, closeAllMenus]);

    const handleMobileSupportClick = useCallback((e) => {
        if (!supportLink) {
            e.preventDefault();
            return;
        }

        e.preventDefault();
        openTelegramModal(supportLink, { actionLabel: 'Открыть поддержку' });
        closeAllMenus();
    }, [supportLink, openTelegramModal, closeAllMenus]);

    const toggleMenu = () => {
        setIsMenuOpen((prev) => !prev);
    };

    const releaseTopControlState = useCallback((event) => {
        const element = event.currentTarget;

        window.requestAnimationFrame(() => {
            element.blur?.();
        });
    }, []);

    useEffect(() => {
        logger.dev('🔗 Header: начинаю загрузку системных ссылок');

        fetchSystemLinks()
            .then(links => {
                logger.dev('🔗 Header: получены ссылки', links);
                setSystemLinks(links);
            })
            .catch(error => {
                logger.error('❌ Header: ошибка при получении ссылок', error);
                setSystemLinks({ bot: '', support: '' });
            });
    }, []);

    useEffect(() => {
        setIsNavigatingToTerms(false);
        closeAllMenus();
    }, [location.pathname, closeAllMenus]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                closeAllMenus();
            }
        };
        window.addEventListener('keydown', onKeyDown);

        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [closeAllMenus]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                desktopMenuRef.current && !desktopMenuRef.current.contains(event.target) &&
                (!dropdownRef.current || !dropdownRef.current.contains(event.target))
            ) {
                setIsDesktopMenuOpen(false);
            }
        };
        if (isDesktopMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDesktopMenuOpen]);

    useLayoutEffect(() => {
        if (!isDesktopMenuOpen || !headerRef.current) {
            return undefined;
        }

        const headerElement = headerRef.current;

        const updateHeaderMetrics = () => {
            const { width, height } = headerElement.getBoundingClientRect();
            const nextMetrics = {
                width: Math.round(width),
                height: Math.round(height),
            };

            setHeaderMetrics((currentMetrics) => (
                currentMetrics.width === nextMetrics.width && currentMetrics.height === nextMetrics.height
                    ? currentMetrics
                    : nextMetrics
            ));
        };

        updateHeaderMetrics();

        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }

        const resizeObserver = new ResizeObserver(() => {
            updateHeaderMetrics();
        });

        resizeObserver.observe(headerElement);

        return () => {
            resizeObserver.disconnect();
        };
    }, [isDesktopMenuOpen]);

    useLayoutEffect(() => {
        if (!isDesktopMenuOpen || !dropdownBodyRef.current) {
            return undefined;
        }

        const dropdownBodyElement = dropdownBodyRef.current;

        const updateDropdownMetrics = () => {
            const nextWidth = Math.max(dropdownBodyElement.offsetWidth, 1);
            const nextHeight = Math.max(dropdownBodyElement.offsetHeight, MIN_DESKTOP_MENU_HEIGHT);

            setDesktopDropdownWidth((currentWidth) => (currentWidth === nextWidth ? currentWidth : nextWidth));
            setDesktopDropdownHeight((currentHeight) => (currentHeight === nextHeight ? currentHeight : nextHeight));
        };

        updateDropdownMetrics();

        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }

        const resizeObserver = new ResizeObserver(() => {
            updateDropdownMetrics();
        });

        resizeObserver.observe(dropdownBodyElement);

        return () => {
            resizeObserver.disconnect();
        };
    }, [isDesktopMenuOpen]);

    useEffect(() => {
        const onResize = () => {
            if (window.innerWidth > 700) {
                setIsMenuOpen(false);
            } else {
                setIsDesktopMenuOpen(false);
            }
        };
        window.addEventListener('resize', onResize, { passive: true });

        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, []);

    useEffect(() => {
        let frameId = 0;

        const updateScrolledState = () => {
            frameId = 0;
            const nextScrolled = window.pageYOffset > 0;
            setIsScrolled((currentScrolled) => (currentScrolled === nextScrolled ? currentScrolled : nextScrolled));
        };

        const onScroll = () => {
            if (frameId !== 0) {
                return;
            }

            frameId = window.requestAnimationFrame(updateScrolledState);
        };

        updateScrolledState();
        window.addEventListener('scroll', onScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', onScroll);
            if (frameId !== 0) {
                window.cancelAnimationFrame(frameId);
            }
        };
    }, []);

    useEffect(() => {
        const classAction = isMenuOpen ? 'add' : 'remove';
        document.body.classList[classAction]('menu-open');
        document.documentElement.classList[classAction]('menu-open');

        if (!isMenuOpen) {
            return () => {
                document.body.classList.remove('menu-open');
                document.documentElement.classList.remove('menu-open');
            };
        }

        const scrollY = window.scrollY || window.pageYOffset || 0;
        const previousBodyStyles = {
            left: document.body.style.left,
            overflow: document.body.style.overflow,
            position: document.body.style.position,
            right: document.body.style.right,
            top: document.body.style.top,
            width: document.body.style.width,
        };
        const previousRootOverflow = document.documentElement.style.overflow;

        document.documentElement.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
        document.body.style.overflow = 'hidden';

        const focusFrameId = window.requestAnimationFrame(() => {
            const nav = document.getElementById('mobile-nav');
            const firstFocusable = nav?.querySelector('a[href], button:not([disabled]), [tabindex="0"]');
            firstFocusable?.focus({ preventScroll: true });
        });

        return () => {
            window.cancelAnimationFrame(focusFrameId);
            document.body.style.left = previousBodyStyles.left;
            document.body.style.overflow = previousBodyStyles.overflow;
            document.body.style.position = previousBodyStyles.position;
            document.body.style.right = previousBodyStyles.right;
            document.body.style.top = previousBodyStyles.top;
            document.body.style.width = previousBodyStyles.width;
            document.documentElement.style.overflow = previousRootOverflow;
            document.body.classList.remove('menu-open');
            document.documentElement.classList.remove('menu-open');
            window.scrollTo(0, scrollY);
        };
    }, [isMenuOpen]);

    const renderHeaderActionContent = useCallback((definition, variant) => {
        const iconConfig = definition.icons?.[variant];
        const IconComponent = iconConfig ? HEADER_ICON_COMPONENTS[iconConfig.name] : null;

        return (
            <>
                {IconComponent ? <IconComponent {...(iconConfig.props || {})} className={iconConfig.className} /> : null}
                {definition.label}
            </>
        );
    }, []);

    const createHeaderActionVariant = useCallback((definition, variant) => {
        const baseConfig = {
            className: definition.classNames[variant],
            title: definition.title,
            content: renderHeaderActionContent(definition, variant),
        };

        if (definition.destination.kind === 'bot') {
            return {
                ...baseConfig,
                type: 'anchor',
                href: botLink || undefined,
                ariaDisabled: !botLink,
                style: !botLink ? disabledLinkStyle : undefined,
                onClick: handleBotOpen,
            };
        }

        if (definition.destination.kind === 'support') {
            return {
                ...baseConfig,
                type: 'anchor',
                href: supportLink || undefined,
                ariaDisabled: !supportLink,
                style: !supportLink ? disabledLinkStyle : undefined,
                onClick: variant === 'mobile' ? handleMobileSupportClick : handleSupportClick,
            };
        }

        if (definition.destination.kind === 'route') {
            return {
                ...baseConfig,
                type: 'link',
                to: definition.destination.to,
                onClick: definition.key === 'terms'
                    ? () => {
                        handleTermsNavigation();
                        closeAllMenus();
                    }
                    : closeAllMenus,
            };
        }

        return null;
    }, [botLink, closeAllMenus, disabledLinkStyle, handleBotOpen, handleMobileSupportClick, handleSupportClick, handleTermsNavigation, renderHeaderActionContent, supportLink]);

    const headerActions = useMemo(() => {
        return HEADER_ACTION_DEFINITIONS.map((definition) => {
            const hidden = pageConfig.hiddenActionKeys.includes(definition.key);

            return {
                key: definition.key,
                hidden,
                desktop: !hidden && definition.placements.desktopMenu
                    ? createHeaderActionVariant(definition, 'desktop')
                    : null,
                mobile: !hidden && definition.placements.mobileMenu
                    ? createHeaderActionVariant(definition, 'mobile')
                    : null,
            };
        });
    }, [createHeaderActionVariant, pageConfig.hiddenActionKeys]);

    const renderHeaderAction = (action, variant) => {
        if (action.hidden || !action[variant]) return null;

        const config = action[variant];
        const sharedProps = {
            key: `${action.key}-${variant}`,
            className: config.className,
            title: config.title,
            onClick: config.onClick,
            style: config.style,
            'data-menu-action': action.key,
            'data-menu-variant': variant,
        };

        if (variant === 'mobile') {
            sharedProps.tabIndex = isMenuOpen ? 0 : -1;
        }

        if (config.type === 'link') {
            return (
                <Link to={config.to} {...sharedProps}>
                    {config.content}
                </Link>
            );
        }

        return (
            <a href={config.href} aria-disabled={config.ariaDisabled} {...sharedProps}>
                {config.content}
            </a>
        );
    };

    const desktopActions = headerActions.filter((action) => !action.hidden && action.desktop);
    const hasDesktopMenuActions = desktopActions.length > 0;

    useEffect(() => {
        if (!hasDesktopMenuActions && isDesktopMenuOpen) {
            setIsDesktopMenuOpen(false);
        }
    }, [hasDesktopMenuActions, isDesktopMenuOpen]);

    const hasDesktopMenuShell = isDesktopMenuOpen && hasDesktopMenuActions && headerMetrics.width > 0 && headerMetrics.height > 0;
    const desktopMenuShellHeight = hasDesktopMenuShell ? headerMetrics.height + desktopDropdownHeight : 0;
    const desktopMenuShellFillPath = hasDesktopMenuShell
        ? buildDesktopMenuShellPath(headerMetrics.width, headerMetrics.height, desktopDropdownWidth, desktopDropdownHeight)
        : '';
    const desktopMenuShellBorderPath = hasDesktopMenuShell
        ? buildDesktopMenuShellBorderPath(headerMetrics.width, headerMetrics.height, desktopDropdownWidth, desktopDropdownHeight)
        : '';
    const desktopMenuShellClipPath = hasDesktopMenuShell ? `path('${desktopMenuShellFillPath}')` : undefined;

    return (
        <>
            <div
                className={`mobile-menu-backdrop ${isMenuOpen ? 'open' : ''}`}
                aria-hidden={!isMenuOpen}
                onClick={closeAllMenus}
            />

            <header
                ref={headerRef}
                className={`${isMenuOpen ? 'header--menu-open' : ''} ${isDesktopMenuOpen ? 'header--desktop-menu-open' : ''} ${isScrolled ? 'header--scrolled' : ''}`.trim()}
                style={{ '--desktop-menu-width': `${desktopDropdownWidth}px` }}
            >
            {hasDesktopMenuShell && (
                <>
                    <div
                        className="desktop-menu-shell-shadow"
                        aria-hidden="true"
                        style={{
                            height: `${desktopMenuShellHeight}px`,
                        }}
                    >
                        <svg className="desktop-menu-shell-shadow__shape" viewBox={`0 0 ${headerMetrics.width} ${desktopMenuShellHeight}`}>
                            <defs>
                                <filter id={DESKTOP_MENU_SHELL_SHADOW_FILTER_ID} x="-12%" y="-8%" width="124%" height="132%" colorInterpolationFilters="sRGB">
                                    <feGaussianBlur in="SourceAlpha" stdDeviation="4" result="blurEdge" />
                                    <feOffset in="blurEdge" dx="0" dy="2" result="offsetEdge" />
                                    <feFlood floodColor="#000000" floodOpacity="0.14" result="colorEdge" />
                                    <feComposite in="colorEdge" in2="offsetEdge" operator="in" result="shadowEdge" />
                                    <feGaussianBlur in="SourceAlpha" stdDeviation="15" result="blurSoft" />
                                    <feOffset in="blurSoft" dx="0" dy="14" result="offsetSoft" />
                                    <feFlood floodColor="#000000" floodOpacity="0.2" result="colorSoft" />
                                    <feComposite in="colorSoft" in2="offsetSoft" operator="in" result="shadowSoft" />
                                    <feGaussianBlur in="SourceAlpha" stdDeviation="36" result="blurDepth" />
                                    <feOffset in="blurDepth" dx="0" dy="30" result="offsetDepth" />
                                    <feFlood floodColor="#000000" floodOpacity="0.16" result="colorDepth" />
                                    <feComposite in="colorDepth" in2="offsetDepth" operator="in" result="shadowDepth" />
                                    <feMerge>
                                        <feMergeNode in="shadowEdge" />
                                        <feMergeNode in="shadowSoft" />
                                        <feMergeNode in="shadowDepth" />
                                    </feMerge>
                                </filter>
                            </defs>
                            <path className="desktop-menu-shell-shadow__path" d={desktopMenuShellFillPath} filter={`url(#${DESKTOP_MENU_SHELL_SHADOW_FILTER_ID})`} />
                        </svg>
                    </div>

                    <div
                        className="desktop-menu-shell"
                        aria-hidden="true"
                        style={{
                            height: `${desktopMenuShellHeight}px`,
                            clipPath: desktopMenuShellClipPath,
                            WebkitClipPath: desktopMenuShellClipPath,
                        }}
                    >
                        <svg className="desktop-menu-shell__shape" viewBox={`0 0 ${headerMetrics.width} ${desktopMenuShellHeight}`}>
                            <path className="desktop-menu-shell__border" d={desktopMenuShellBorderPath} />
                        </svg>
                    </div>
                </>
            )}

            <Link to="/" className="logo" aria-label="На главную" onClick={closeAllMenus}>
                <span className="logo__text">
                    <span className="logo__protocol">PROTOCOL</span>
                </span>
                <span className="logo-frame">
                    <img src={logoImage} alt="" loading="eager" decoding="sync" fetchpriority="high" width="56" height="56" />
                </span>
                <span className="logo__goida">GOIDA</span>
            </Link>

            {(pageConfig.showDesktopBotButton || hasDesktopMenuActions) && (
                <div className="nav-buttons hidden sm:flex items-center" ref={desktopMenuRef}>
                    {pageConfig.showDesktopBotButton && (
                        <button
                            type="button"
                            className="btn-desktop-bot header-top-control"
                            onClick={handleBotOpen}
                            onPointerUp={releaseTopControlState}
                            aria-label="Перейти к боту"
                            title="Перейти к боту"
                            disabled={!botLink}
                        >
                            <PlaneIcon size={18} />
                        </button>
                    )}

                    {hasDesktopMenuActions && (
                        <button
                            type="button"
                            className={`btn-desktop-menu header-top-control ${isDesktopMenuOpen ? 'active' : ''}`}
                            onClick={() => setIsDesktopMenuOpen(!isDesktopMenuOpen)}
                            onPointerUp={releaseTopControlState}
                            aria-expanded={isDesktopMenuOpen}
                            aria-label={isDesktopMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
                            title={isDesktopMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
                            aria-haspopup="menu"
                        >
                            <MenuToggleIcon className="menu-control-icon" />
                        </button>
                    )}
                </div>
            )}

            {isDesktopMenuOpen && hasDesktopMenuActions && (
                <div className="desktop-dropdown" ref={dropdownRef}>
                    <div className="desktop-dropdown-body" ref={dropdownBodyRef}>
                        {desktopActions.map((action) => renderHeaderAction(action, 'desktop'))}
                    </div>
                </div>
            )}

            {showBackButton ? (
                <Link to="/" className="btn btn-home header-top-control" title="На главную" onClick={releaseTopControlState} onPointerUp={releaseTopControlState}>
                    <LeftArrowIcon className="icon--block-center" />
                </Link>
            ) : pageConfig.showMobileMenuToggle && (
                <button
                    ref={menuToggleRef}
                    type="button"
                    className={`menu-toggle header-top-control ${isMenuOpen ? 'active' : ''}`}
                    onClick={toggleMenu}
                    onPointerUp={releaseTopControlState}
                    aria-label={isMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
                    aria-expanded={isMenuOpen}
                    aria-controls="mobile-nav"
                    aria-haspopup="true"
                >
                    <MenuToggleIcon className="menu-control-icon" />
                </button>
            )}

                <nav
                    id="mobile-nav"
                    className={`mobile-nav ${isMenuOpen ? 'open' : ''}`}
                    aria-hidden={!isMenuOpen}
                >
                    {headerActions.map((action) => renderHeaderAction(action, 'mobile'))}
                </nav>
            </header>
        </>
    );
}

export default Header;
